import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, ExternalLink, Bot, Loader2, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

/**
 * AdminAgentSearch — iter128.1
 * Replaces the removed auto-cross-tenant leak. System admins can search
 * any tenant's AI Employees by name / chatbot_id / owner email and open
 * them in the standard agent detail page (backend returns
 * ``user_role: "admin"`` so the admin can view + edit as needed).
 */
export const AdminAgentSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runSearch = async (query) => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/admin/agents/search', { params: query ? { q: query } : {} });
      setResults(r.data?.results || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Prime the list with the most-recent agents on first mount.
  useEffect(() => { runSearch(''); }, []);

  // Debounced live search as the admin types.
  useEffect(() => {
    const t = setTimeout(() => { runSearch(q); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const openAgent = (row) => {
    if (!row?.chatbot_id) return;
    navigate(`/dashboard/chatbots/${row.chatbot_id}`);
  };

  // iter128.2 — ghost login as the owner of a specific agent so admins can
  // investigate tenant-scoped bugs in the exact same UI the owner sees.
  const ghostLoginAsOwner = async (row, e) => {
    if (e) e.stopPropagation();
    if (!row?.owner_user_id) {
      toast.error('No owner linked to this agent');
      return;
    }
    const confirmed = window.confirm(
      `Ghost-login as ${row.owner_email || row.owner_name || 'agent owner'}?\n\n` +
      `You'll be signed in as this user for 30 minutes and every action will be attributed to them (with your admin email attached in the audit log). Click OK to proceed.`
    );
    if (!confirmed) return;
    try {
      const res = await api.post(`/admin/users/${row.owner_user_id}/impersonate`);
      const currentAdmin = localStorage.getItem('harmonix_st');
      if (currentAdmin) sessionStorage.setItem('harmonix_ghost_return', currentAdmin);
      localStorage.setItem('harmonix_st', res.data.session_token);
      sessionStorage.setItem('harmonix_ghost_meta', JSON.stringify({
        target_email: res.data.user?.email,
        impersonated_by_email: res.data.impersonated_by_email,
        expires_at: res.data.expires_at,
      }));
      toast.success(`Now viewing Harmonix as ${res.data.user?.email}`);
      window.location.href = `/dashboard/chatbots/${row.chatbot_id}`;
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Ghost login failed');
    }
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="admin-agent-search-card">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Bot className="w-5 h-5 text-blue-600" />
          View any agent (cross-tenant)
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Search by agent name, chatbot ID, or owner email. Opens the agent detail page — system admins have full access.
        </p>
      </CardHeader>
      <CardContent>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search agents (name, id, or owner email)..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
            data-testid="admin-agent-search-input"
          />
        </div>

        {error && (
          <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 mb-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            No agents match &ldquo;{q}&rdquo;
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {results.map((row) => (
              <div
                key={row.chatbot_id}
                className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-blue-300 transition-all cursor-pointer"
                onClick={() => openAgent(row)}
                data-testid={`admin-agent-row-${row.chatbot_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 truncate">{row.name || row.chatbot_id}</p>
                    {row.status && (
                      <Badge className="bg-slate-200 text-slate-700 border-slate-300 text-xs">{row.status}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {row.owner_email || row.owner_name || 'Unknown owner'}
                    {row.owner_org ? ` — ${row.owner_org}` : ''}
                    <span className="ml-2 font-mono opacity-70">{row.chatbot_id}</span>
                  </p>
                </div>
                <Button size="sm" variant="outline" className="ml-3 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`admin-agent-open-${row.chatbot_id}`}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                </Button>
                {row.owner_user_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                    onClick={(e) => ghostLoginAsOwner(row, e)}
                    title={`Ghost-login as ${row.owner_email || 'owner'}`}
                    data-testid={`admin-agent-ghost-${row.chatbot_id}`}
                  >
                    <UserCog className="w-3.5 h-3.5 mr-1" /> Login as owner
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAgentSearch;
